import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import HardSkillProp from '../challenges/propTypes/hardSkillProp';
import TrophyProp from '../journey/propTypes/trophyProp';
import PerformanceProp from './propTypes/performanceProp';
import ChartsProp from './propTypes/chartsProp';
import CertificateProp from '../academy/propTypes/certificateProp';
import AppProductProp from './propTypes/appProductProp';
import StripeCardProp from './propTypes/StripeCardProp';
import Request from '../../services/request';
import Notifications from '../../services/notifications';
import { compose, PROFILE_TABS } from '../../services/utilityFunctions';
import { useScrollAnchor } from '../../services/customHooks';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import MainPanel from './mainPanel/MainPanel';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import CurrencyProp from './propTypes/currencyProp';
import VatTypesProp from '../../commonComponents/forms/Address/VatTypesProp';
import SidePanel from './sidePanel/SidePanel';
import AddressProp from '../../commonComponents/propTypes/addressProp';

/**
 * User profile main page
 */
function Profile(props) {
  const {
    t,
    _csrf,
    activeTab: activeTabParam,
    cards: paramCards,
    availableCountries,
    countries,
    vatTypes,
    languages,
    allLanguages,
    availableCurrencies,
    hardSkills,
    stats,
    customTrophies,
    certificates,
    appProducts,
    hideSidePanel,
    onChange,
    userLanguage,
    directSellingActive,
    organization,
    isInJourney,
    shouldRefreshData,
    onSkipStep,
  } = props;

  const { setLoggedUser, loggedUser: member } = useContext(LoggedUserContext);

  const [memberState, setMemberState] = useState(member);

  useEffect(() => {
    if (isInJourney) {
      axios
        .get(`/api/user/${member._id}`, {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Organization',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        })
        .then(({ data: response }) => {
          setMemberState(response.data);
        })
        .catch(err => {
          console.log(err);
        });
    }
  }, []);

  const workplaceLocationEnabled =
    organization?.workplaceLocations?.enabled || false;
  const workplaceLocationList = organization?.workplaceLocations?.list || [];

  const [cards, setCards] = useState(paramCards.slice());
  const [activeTab, setActiveTab] = useState(activeTabParam);
  const { contentElement, scrollToContent } = useScrollAnchor();

  const handleTabSelection = tab => {
    scrollToContent();
    setActiveTab(tab);
  };

  const refreshData = () => {
    new Request()
      .get(`/user/${member._id}/json`)
      .done(({ data }) => {
        setLoggedUser(data);
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchProfile');
      });
  };
  const [prevUserPage, setPrevUserPage] = useState({
    error: null,
    userPageName: null,
  });

  const handleSubmit = (changes, subSection, changes2, subSection2) => {
    let params = { ...changes };
    if (subSection) {
      params = { [subSection]: { ...member[subSection], ...changes } };
    }
    if (changes2 && subSection2) {
      params = {
        [subSection2]: { ...member[subSection2], ...changes2 },
      };
      if (subSection) {
        params = {
          ...params,
          [subSection]: { ...member[subSection], ...changes },
        };
      }
    }

    // Check if error element for NIF already exist
    const existElement = document.getElementById('NIF-error');
    let placeholder = null;
    // Check if placeholder is sent
    if (params?.personalInfo?.placeholder?.length > 0) {
      placeholder = params.personalInfo.placeholder;
      // remove from params before sending to API
      delete params.personalInfo.placeholder;
    }

    new Request(_csrf)
      .put('/profile/edit', params)
      .done(() => {
        if (Object.keys(params).includes('language')) {
          window.location.reload();
        } else {
          // If success and error exist, remove it
          if (existElement) {
            existElement.remove();
          }
          Notifications.showNotificationSuccess(
            t('success'),
            t('success_update'),
          );
          // When the onChange is being used from Journeys, we should not refresh data of the profile
          if (onChange) {
            onChange();
            if (shouldRefreshData) {
              refreshData();
            }
          } else {
            refreshData();
          }
        }
      })
      .fail(err => {
        if (
          err &&
          err.responseJSON?.error === 'ERROR_USER_PAGE_NAME_ALREADY_EXISTS'
        ) {
          setPrevUserPage({
            error: 'ERROR_USER_PAGE_NAME_ALREADY_EXISTS',
            userPageName: member?.userPageName || '',
          });
        } else {
          setPrevUserPage({ error: null, userPageName: null });
        }
        if (
          err &&
          err.responseJSON?.error === 'ERROR_INVALID_TAX_ID_NUMBER' &&
          !existElement
        ) {
          let errorMessage = t('ERROR_INVALID_TAX_ID_NUMBER');
          // Add the placeholder to the error message to show the user how should the NIF be
          if (placeholder) {
            errorMessage += ` - (Ex: ${placeholder})`;
          }
          // IF error = ERROR_INVALID_TAX_ID_NUMBER add label error by jquery
          $('#NIF').after(
            `<label
              id="NIF-error"
              className="error"
              htmlFor="NIF"
              style='color: var(--danger)'
              >
                ${errorMessage}
              </label>`,
          );
        }
        Notifications.showErrorFromRequest(err, 'error_profileUpdate');
      });
  };

  return (
    <>
      {!hideSidePanel && (
        <div className="col-lg-4">
          <SidePanel
            directSellingActive={directSellingActive}
            member={memberState}
            activeTab={activeTab}
            onTabSelection={handleTabSelection}
          />
        </div>
      )}
      <div className={hideSidePanel ? undefined : 'col-lg-8'}>
        <MainPanel
          ref={contentElement}
          activeTab={activeTab}
          member={memberState}
          onMemberChange={handleSubmit}
          onCardsChange={setCards}
          cards={cards}
          countries={countries}
          availableCountries={availableCountries}
          vatTypes={vatTypes}
          languages={languages}
          allLanguages={allLanguages}
          currencies={availableCurrencies}
          hardSkills={hardSkills}
          stats={stats}
          customTrophies={customTrophies}
          certificates={certificates}
          appProducts={appProducts}
          userLang={userLanguage}
          directSellingActive={directSellingActive}
          _csrf={_csrf}
          workplaceLocations={{
            enabled: workplaceLocationEnabled,
            list: workplaceLocationList,
          }}
          previousUserPageName={prevUserPage}
          organizationCompetenciesDomains={
            organization.competenciesDomains || []
          }
          organization={organization}
          isInJourney={isInJourney}
          onSkipStep={onSkipStep}
        />
      </div>
    </>
  );
}

Profile.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  availableCountries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  userLanguage: PropTypes.string.isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  activeTab: PropTypes.oneOf(PROFILE_TABS).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  allLanguages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  availableCurrencies: PropTypes.arrayOf(
    PropTypes.shape(CurrencyProp.propType),
  ),
  hardSkills: PropTypes.arrayOf(PropTypes.shape(HardSkillProp.propType))
    .isRequired,
  customTrophies: PropTypes.arrayOf(PropTypes.shape(TrophyProp.propType))
    .isRequired,
  stats: PropTypes.shape({
    performance: PropTypes.shape(PerformanceProp.propType),
    charts: PropTypes.shape(ChartsProp.propType),
  }).isRequired,
  certificates: PropTypes.arrayOf(PropTypes.shape(CertificateProp.propType))
    .isRequired,
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  hideSidePanel: PropTypes.bool,
  onChange: PropTypes.func,
  directSellingActive: PropTypes.bool.isRequired,
  organization: PropTypes.shape({
    workplaceLocations: PropTypes.shape({
      enabled: PropTypes.bool,
      list: PropTypes.arrayOf(PropTypes.shape(AddressProp.propType)),
    }),
    competenciesDomains: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        _id: PropTypes.string,
      }),
    ),
    cryptoCurrencies: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isInJourney: PropTypes.bool,
  shouldRefreshData: PropTypes.bool,
  onSkipStep: PropTypes.func,
};

Profile.defaultProps = {
  hideSidePanel: false,
  onChange: null,
  availableCurrencies: [],
  isInJourney: false,
  shouldRefreshData: false,
  onSkipStep: null,
};

export default React.memo(compose(withCSRF, translate)(Profile));
